import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { displayScore, publishedBandStyle } from '../../../services/trustBands';

/**
 * The shared furniture of a trust score panel.
 *
 * Design time and run time are one instrument reading two kinds of evidence, so they are drawn by
 * one set of parts rather than two that drift. Every colour resolves through publishedBandStyle()
 * and every size through a --v2-trust-* token; nothing here carries a literal.
 *
 * Extracted verbatim from TrustScorePanel so the design-time surface renders identically — the
 * only change is --v2-surface, which is defined nowhere in styles/ and was silently resolving to
 * no background at all.
 */

/** The dial. Band drives the arc, the number and the label — one source, no hand-tinting. */
export function Dial({ score, style, label = 'Trust score' }) {
  // displayScore, not a local finiteness check: the ring is sized for at most three digits, and a
  // caller handing it an unrounded score used to render "77.91" across a 128px dial. Rounding is
  // the band service's rule, applied here so no surface can opt out of it by accident.
  const value = displayScore(score);
  const r = 52, C = 2 * Math.PI * r;
  return (
    <div style={{ position: 'relative', width: 128, height: 128, flex: 'none' }}>
      <svg width="128" height="128" viewBox="0 0 128 128" role="img"
        aria-label={`${label} ${value == null ? 'not yet scored' : value}, ${style.label}`}>
        <circle cx="64" cy="64" r={r} fill="none" stroke="var(--v2-bg-muted)" strokeWidth="11" />
        {value != null && (
          <circle cx="64" cy="64" r={r} fill="none" stroke={style.color} strokeWidth="11"
            strokeLinecap="round" strokeDasharray={C}
            strokeDashoffset={C - (C * value) / 100} transform="rotate(-90 64 64)" />
        )}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 'var(--v2-trust-score-size)',
          fontWeight: 'var(--v2-trust-score-weight)',
          letterSpacing: 'var(--v2-trust-score-tracking)', lineHeight: 1,
          color: style.ink, fontVariantNumeric: 'tabular-nums' }}>
          {value == null ? '—' : value}
        </div>
        <div style={{ fontSize: 'var(--v2-trust-bandlabel-size)',
          fontWeight: 'var(--v2-trust-bandlabel-weight)',
          letterSpacing: 'var(--v2-trust-bandlabel-tracking)', marginTop: 4,
          color: style.ink, textTransform: 'uppercase' }}>
          {style.label}
        </div>
      </div>
    </div>
  );
}

/** One factor, its bar coloured by the band its own value falls in. */
export function FactorRow({ factor, nameOf }) {
  const v = Number(factor.value);
  const fs = publishedBandStyle(v);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 84px 34px', gap: 10,
      alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--v2-border-subtle)' }}>
      <div>
        <div style={{ fontSize: 'var(--v2-trust-row-size)', fontWeight: 550 }}>
          {nameOf(factor.factorKey)}
        </div>
        <div style={{ fontSize: 'var(--v2-trust-caption-size)', color: 'var(--v2-text-muted)' }}>
          {Math.round(Number(factor.weight) * 100)}% weight
          {factor.contested ? ' · contested' : ''}
        </div>
      </div>
      <div style={{ height: 7, borderRadius: 4, background: 'var(--v2-bg-muted)', overflow: 'hidden' }}>
        <i style={{ display: 'block', height: '100%', borderRadius: 4,
          width: `${Math.max(0, Math.min(100, v))}%`, background: fs.color }} />
      </div>
      <div style={{ textAlign: 'right', fontSize: 'var(--v2-trust-value-size)',
        fontWeight: 'var(--v2-trust-value-weight)', color: fs.ink,
        fontVariantNumeric: 'tabular-nums' }}>{displayScore(v)}</div>
    </div>
  );
}

/** One line of the deduction ladder: what it was, and what it cost. */
export function DeductionRow({ name, points }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10,
      padding: '7px 0', borderBottom: '1px solid var(--v2-border-subtle)' }}>
      <span style={{ fontSize: 'var(--v2-trust-row-size)', fontWeight: 550, flex: 1 }}>{name}</span>
      <span style={{ fontSize: 'var(--v2-trust-value-size)',
        fontWeight: 'var(--v2-trust-value-weight)',
        color: 'var(--v2-band-critical-ink)', fontVariantNumeric: 'tabular-nums' }}>
        &minus;{Number(points).toFixed(1)}
      </span>
    </div>
  );
}

export function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 'var(--v2-trust-caption-size)',
      fontWeight: 'var(--v2-trust-caption-weight)', color: 'var(--v2-text-muted)',
      textTransform: 'uppercase', letterSpacing: 'var(--v2-trust-caption-tracking)',
      marginBottom: 6 }}>{children}</div>
  );
}

export function Pill({ children, tone }) {
  const warn = tone === 'warn';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 'var(--v2-trust-pill-size)', fontWeight: 'var(--v2-trust-pill-weight)',
      letterSpacing: 'var(--v2-trust-pill-tracking)', padding: '3px 8px',
      borderRadius: 'var(--v2-radius-pill)',
      border: '1px solid',
      background: warn ? 'var(--v2-warning-soft)' : 'var(--v2-bg-muted)',
      color: warn ? 'var(--v2-warning)' : 'var(--v2-text-secondary)',
      borderColor: warn ? 'var(--v2-warning)' : 'var(--v2-border-default)' }}>
      {warn && <AlertTriangle size={9} />}{children}
    </span>
  );
}

/** The panel shell, so both surfaces sit in the same box. */
export const PANEL_STYLE = {
  border: '1px solid var(--v2-border-subtle)',
  borderRadius: 'var(--v2-radius-md)',
  background: 'var(--v2-bg-surface)',
  padding: 16,
  fontFamily: 'var(--v2-font-ui)',
};

/** The header control, so Rescore and Recalculate are the same button. */
export const CONTROL_STYLE = {
  marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
  fontFamily: 'var(--v2-font-ui)',
  fontSize: 'var(--v2-trust-control-size)',
  fontWeight: 'var(--v2-trust-control-weight)',
  lineHeight: 1,
  padding: '7px 11px', borderRadius: 'var(--v2-radius-sm)', cursor: 'pointer',
  border: '1px solid var(--v2-border-default)',
  background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-primary)',
};

/**
 * Where a score's evidence came from, in words.
 *
 * The method numbers its evidence levels internally. That numbering is ours — it tells a reader
 * nothing on its own and invites them to wonder what the other numbers are. What they need to know
 * is what the score rests on, so that is what the screen says.
 */
export function evidenceLabel(rung) {
  switch (Number(rung)) {
    case 1:  return 'From your declaration';
    case 2:  return 'From what discovery found';
    case 3:  return 'From this agent\u2019s governed runs';
    case 4:  return 'From verified runs';
    default: return null;
  }
}

/** Operator wording for a key. No internal vocabulary reaches the screen. */
const NAMES = {
  identity_binding: 'Identity binding',
  permission_scope: 'Permission scope',
  data_exposure: 'Data exposure',
  governance_coverage: 'Governance coverage',
  provenance: 'Provenance',
  declared_posture: 'Declared posture',
  blocked_actions: 'Blocked actions',
  warnings: 'Warnings',
  audit_findings: 'Audit findings',
};

export function factorName(key) {
  if (NAMES[key]) return NAMES[key];
  // A key this build does not know about still gets readable wording rather than a raw token.
  // bandLabel() would have answered "Not yet scored" here, which is a different statement entirely.
  return String(key || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
